import { useCallback, useEffect, useMemo, useState } from 'react';
import type { EstimateAlgorithm, Report, StoredReport } from '@wrp/shared';
import * as api from '../api';
import { useApp, useStreamEvent } from '../store';

export const FEED_LIMIT = 50;

export const ALGORITHMS: Array<{ id: EstimateAlgorithm; label: string }> = [
  { id: 'wknn', label: 'WKNN' },
  { id: 'knn', label: 'KNN' },
  { id: 'multilateration', label: 'Multilateration' },
];

export function parseNumber(value: string | undefined): number | null {
  if (value === undefined || value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Recent reports, newest first, kept current by the SSE stream. */
export function useReportFeed(): StoredReport[] {
  const { reportError } = useApp();
  const [reports, setReports] = useState<StoredReport[]>([]);

  const load = useCallback(async () => {
    try {
      const res = await api.listReports({ limit: FEED_LIMIT });
      setReports(res.items);
    } catch (error) {
      reportError(error, 'Failed to load reports');
    }
  }, [reportError]);

  useEffect(() => {
    void load();
  }, [load]);

  useStreamEvent<StoredReport>('report', (report) => {
    setReports((prev) => [report, ...prev.filter((r) => r.id !== report.id)].slice(0, FEED_LIMIT));
  });

  return reports;
}

/** Report ids that have been turned into fingerprints (the stored report's own flag goes stale over SSE). */
export function useRecordedReportIds(): Set<string> {
  const { fingerprints } = useApp();
  return useMemo(() => new Set(fingerprints.map((fp) => fp.report_id)), [fingerprints]);
}

/** Strips a stored report down to what the estimate endpoint needs, optionally adding ground truth. */
export function toInlineReport(
  stored: StoredReport,
  groundTruth?: { floor_id: string; x: number; y: number },
): Report {
  return {
    device_id: stored.device_id,
    source: 'scan',
    aps: stored.aps
      .filter((ap) => typeof ap.signal_strength === 'number')
      .map((ap) => ({ bssid: ap.bssid, signal_strength: ap.signal_strength })),
    ...(groundTruth ? { ground_truth: groundTruth } : {}),
  };
}
